import React, { useState, useEffect } from 'react';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import { useProjectScenarios } from '../../../context/ProjectScenariosContext';
import { UserRoles } from '../../../constants/UserRoles';
import RoleGuard from '../../common/RoleGuard';
import ScenarioSwitcher from './ScenarioSwitcher';
import { ProjectsScenariosRestDataProvider } from '../../../data/ProjectScenariosDataProvider';
import { useTranslation } from 'react-i18next';

const generateGuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });

interface ProjectRisksProps {
  scenarioId: string;
  setScenarioId: (id: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const ProjectRisks: React.FC<ProjectRisksProps> = ({ scenarioId, setScenarioId, onDirtyChange }) => {
  const { t } = useTranslation(['common', 'screens']);
  const { scenarios, setScenarios } = useProjectScenarios();
  const scenario = scenarios.find(s => s.id === scenarioId) || scenarios[0];
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const provider = new ProjectsScenariosRestDataProvider();

  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  if (!scenario) return <SubsectionPanel title={t('screens:projectRisks.title')}><div>{t('screens:projects.noScenarioSelected')}</div></SubsectionPanel>;

  const phaseOptions = (scenario?.ganttChartPlan?.phases || []).map(phase => ({ label: phase.name, value: phase.id }));

  const columns: DetailedListColumn<any>[] = [
    { key: 'name', header: t('screens:projectRisks.columns.name'), dataField: 'name', type: 'string' },
    { key: 'impact', header: t('screens:projectRisks.columns.impact'), dataField: 'impact', type: 'multiline', multiline: true, rows: 3 },
    { key: 'severity', header: t('screens:projectRisks.columns.severity'), dataField: 'severity', type: 'number' },
    { key: 'likelihood', header: t('screens:projectRisks.columns.likelihood'), dataField: 'likelihood', type: 'number' },
    { key: 'owner', header: t('screens:projectRisks.columns.owner'), dataField: 'owner', type: 'string' },
    { key: 'mitigation', header: t('screens:projectRisks.columns.mitigation'), dataField: 'mitigation', type: 'multiline', multiline: true, rows: 3 },
    {
      key: 'phase',
      header: t('screens:projectRisks.columns.phase'),
      dataField: 'phase',
      type: 'select',
      options: phaseOptions,
      render: (item) => {
        const phase = phaseOptions.find(opt => opt.value === item.phase);
        return phase ? phase.label : '';
      }
    },
  ];

  // Ensure all risk items have a stable id
  const riskItemsWithIds = scenario.riskItems.map(item => ({ ...item, id: item.id || generateGuid() }));

  const handleChange = (newRisks: any[]) => {
    setEditing(true);
    setError(null);
    setIsDirty(true);
    try {
      // Ensure all new risks have a stable id and normalize empty fields to null
      const withIds = newRisks.map(item => ({
        ...item,
        id: item.id || generateGuid(),
        name: item.name === '' ? null : item.name,
        impact: item.impact === '' ? null : item.impact,
        owner: item.owner === '' ? null : item.owner,
        mitigation: item.mitigation === '' ? null : item.mitigation,
        severity: item.severity === '' || isNaN(Number(item.severity)) ? null : Number(item.severity),
        likelihood: item.likelihood === '' || isNaN(Number(item.likelihood)) ? null : Number(item.likelihood),
        phase: item.phase === '' ? null : item.phase
      }));
      setScenarios(scenarios.map(s =>
        s.id === scenario.id ? { ...s, riskItems: withIds } : s
      ));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setEditing(false);
    }
  };

  // Save handler for ScenarioSwitcher
  const handleSave = async () => {
    if (!scenario.id) return;
    setSaving(true);
    setIsDirty(false);
    try {
      await provider.update(scenario.id, scenario);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ScenarioSwitcher scenarioId={scenario?.id} setScenarioId={setScenarioId} onSave={handleSave} saving={saving} />
      <SubsectionPanel title={t('screens:projectRisks.title')}>
        {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
        <DetailedListComponent
          dataSource={{ data: riskItemsWithIds, setData: (data) => handleChange(data) }}
          columns={columns}
          rowKey={r => r.id}
          onChange={handleChange}
          addRole={UserRoles.Admin + ", " + UserRoles.Manager}
          editRole={UserRoles.Admin + ", " + UserRoles.Manager}
          deleteRole={UserRoles.Admin + ", " + UserRoles.Manager}
        />
        {editing && <div style={{ marginTop: 12 }}>Saving...</div>}
      </SubsectionPanel>
    </>
  );
};

export default ProjectRisks;
